<template>
  <header id="header" class="header header--homepage">
      <div class="header__inner">
          <!-- Logo - always visible, outside main content div -->
          <router-link to="/" class="header__logo">
              <img 
                src="@/assets/images/logo.svg"
                alt="College Genius logo" 
                class="header__logo-img"
              />
          </router-link>
          
          <div class="header__content">
              <!-- Desktop Navigation -->
              <nav class="nav nav--desktop">
                  <ul class="nav__list">
                      <li class="nav__item">
                          <router-link to="/browse-counselors" class="nav__link">Browse Counselors</router-link>
                      </li>
                      <li class="nav__item">
                          <router-link to="/how-it-works" class="nav__link">How It Works</router-link>
                      </li>
                      <li class="nav__item">
                          <router-link to="/careers" class="nav__link">Careers</router-link>
                      </li>
                      <li class="nav__item">
                          <router-link to="/pricing" class="nav__link">Pricing</router-link>
                      </li>
                      <li class="nav__item" v-if="authed">
                          <router-link to="/messages" class="nav__link">Messages</router-link>
                      </li>
                      <li class="nav__item">
                          <a href="tel:18443436272" class="phone__link">📞 +1(844) 343-6272</a>
                      </li>
                  </ul>
              </nav>
              
              <!-- Desktop auth links -->
              <div class="header__account header__account--desktop">
                  <div v-if="authed" class="user-info">
                      <router-link to="/edit-profile" class="user-name">{{ fullName }}</router-link>
                      <button @click="handleLogout" class="logout-btn">Log Out</button>
                  </div>
                  <ul v-else class="auth-links">
                      <li class="auth-links__item">
                          <router-link to="/auth/login" class="auth-links__link">Sign In</router-link>
                      </li>
                      <li class="auth-links__item">
                          <router-link to="/auth/sign-up" class="auth-links__link btn-secondary">Get Started</router-link>
                      </li>
                  </ul>
              </div>

              <!-- Hamburger menu - mobile only -->
              <div class="header__hamburger">
                  <input type="checkbox" id="menu-toggle" class="menu-toggle" v-model="menuOpen" />
                  <label for="menu-toggle" class="hamburger" @click="toggleMenu">
                    <span></span>
                    <span></span>
                    <span></span>
                  </label>
              </div>

              <!-- Mobile Navigation Menu -->
              <nav class="nav nav--mobile" :class="{ 'nav--open': menuOpen }">
                  <ul class="nav__list">
                      <li class="nav__item">
                          <router-link to="/browse-counselors" class="nav__link" @click="closeMenu">Browse Counselors</router-link>
                      </li>
                      <li class="nav__item">
                          <router-link to="/how-it-works" class="nav__link" @click="closeMenu">How It Works</router-link>
                      </li>
                      <li class="nav__item">
                          <router-link to="/careers" class="nav__link" @click="closeMenu">Careers</router-link>
                      </li>
                      <li class="nav__item">
                          <router-link to="/pricing" class="nav__link" @click="closeMenu">Pricing</router-link>
                      </li>
                      <li class="nav__item" v-if="authed">
                          <router-link to="/messages" class="nav__link" @click="closeMenu">Messages</router-link>
                      </li>
                      <li class="nav__item">
                          <a href="tel:18443436272" class="phone__link">📞 +1(844) 343-6272</a>
                      </li>
                  </ul>
                  
                  <!-- Mobile auth links -->
                  <ul v-if="!authed" class="auth-links auth-links--mobile">
                      <li class="auth-links__item">
                          <router-link to="/auth/login" class="auth-links__link" @click="closeMenu">Sign In</router-link>
                      </li>
                      <li class="auth-links__item">
                          <router-link to="/auth/sign-up" class="auth-links__link btn-secondary" @click="closeMenu">Get Started</router-link>
                      </li>
                  </ul>
                  
                  <!-- Mobile user info -->
                  <div v-if="authed" class="user-info user-info--mobile">
                      <router-link to="/edit-profile" class="user-name" @click="closeMenu">{{ fullName }}</router-link>
                      <button @click="handleLogoutMobile" class="logout-btn">Log Out</button>
                  </div>
              </nav>
          </div>
      </div>
  </header>
</template>

<script>
const API_BASE = process?.env?.VUE_APP_API_BASE_URL ?? '/api';
import Auth from '@/auth/auth.js';
import ProfileApi from '@/auth/profileApi.js';

export default {
  data() {
    return {
      fullName: "",
      authed: false,
      menuOpen: false
    };
  },
  methods: {
    toggleMenu() {
      this.menuOpen = !this.menuOpen;
    },
    closeMenu() {
      this.menuOpen = false;
    },
    async refreshAuth() {
      try {
        this.authed = await Auth.isAuthenticated();
      } catch {
        this.authed = false;
      }
    },
    isAuthenticated() {
      return this.authed;
    },
    async fetchFullName() {
      if (this.authed) {
        try {
          const data = await ProfileApi.getData();
          if (data && data.user) {
            this.fullName = `${data.user.first_name}`;
          }
        } catch (error) {
          console.error("Failed to fetch user data:", error);
        }
      }
    },
    handleLogout() {
      fetch(`${API_BASE}/logout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include'
      })
        .then(response => {
          if (response.ok) {
            Auth.reset();
            this.authed = false;
            this.fullName = '';
            window.location.href = '/';
          }
        })
        .catch(error => console.error("Error during logout:", error));
    },
    handleLogoutMobile() {
      this.closeMenu();
      this.handleLogout();
    }
  },
  async created() {
    await this.refreshAuth();
    await this.fetchFullName();
  }
};
</script>

<style>
/* Base header styles */
.header {
  background-color: #3867ae;
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 9999;
  width: 100%;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
}

.header__inner {
  max-width: 1400px;
  margin: 0 auto;
  padding: 0.5rem 2rem;
  display: flex;
  align-items: center;
  gap: 1.5rem;
  flex-direction: row;
}

.header__content {
  display: flex;
  align-items: center;
  justify-content: space-between;
  min-height: 60px;
  gap: 1.5rem;
  width: 100%;
  overflow: visible;
  flex: 1;
  order: 2;
}

/* Logo styles - always visible */
.header__logo {
  display: block !important;
  flex-shrink: 0 !important;
  z-index: 10001;
  min-width: 50px;
  visibility: visible !important;
  opacity: 1 !important;
  order: 1;
}

.header__logo-img {
  height: 50px;
  width: auto;
  display: block !important;
  visibility: visible !important;
}

/* Hide checkbox */
.menu-toggle {
  display: none;
}

/* Hamburger wrapper - hidden on desktop */
.header__hamburger {
  display: none;
  flex-shrink: 0;
  order: 3;
  margin-left: auto;
}

/* Hamburger icon */
.hamburger {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  width: 28px;
  height: 21px;
  cursor: pointer;
  z-index: 10001;
  transition: transform 0.3s ease;
}

.hamburger span {
  display: block;
  height: 3px;
  background: white;
  border-radius: 2px;
  transition: all 0.3s ease;
}

/* Hamburger animation */
.menu-toggle:checked + .hamburger span:nth-child(1) {
  transform: rotate(45deg) translate(6px, 6px);
}

.menu-toggle:checked + .hamburger span:nth-child(2) {
  opacity: 0;
}

.menu-toggle:checked + .hamburger span:nth-child(3) {
  transform: rotate(-45deg) translate(6px, -6px);
}

/* Desktop navigation */
.nav--desktop {
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 1;
}

/* Mobile navigation - hidden by default */
.nav--mobile {
  display: none;
}

.nav__list {
  display: flex;
  align-items: center;
  list-style: none;
  margin: 0;
  padding: 0;
  gap: 1.5rem;
  flex-wrap: nowrap;
}

.nav__item {
  white-space: nowrap;
  flex-shrink: 0;
}

.nav__link,
.phone__link {
  color: white;
  text-decoration: none;
  font-size: 0.95rem;
  font-weight: 600;
  transition: color 0.3s ease;
  display: inline-block;
}

.nav__link:hover,
.phone__link:hover {
  color: #FFD966;
}

/* Auth links */
.auth-links {
  display: flex;
  list-style: none;
  margin: 0;
  padding: 0;
  gap: 0.75rem;
  flex-shrink: 0;
}

.auth-links__link {
  color: white;
  text-decoration: none;
  font-weight: 600;
  padding: 0.5rem 1rem;
  border-radius: 4px;
  transition: all 0.3s ease;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  white-space: nowrap;
}

.btn-secondary {
  border: 2px solid white;
}

.btn-secondary:hover {
  background-color: white;
  color: #3867ae;
}

/* User info */
.user-info {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex-shrink: 0;
}

.user-name {
  color: white;
  text-decoration: none;
  font-weight: 600;
  transition: color 0.3s ease;
  white-space: nowrap;
}

.user-name:hover {
  color: #FFD966;
}

.logout-btn {
  background: transparent;
  border: 2px solid white;
  color: white;
  padding: 0.5rem 1rem;
  border-radius: 4px;
  cursor: pointer;
  font-weight: 600;
  transition: all 0.3s ease;
  white-space: nowrap;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.logout-btn:hover {
  background-color: white;
  color: #3867ae;
}

/* Desktop account section */
.header__account--desktop {
  display: flex;
  flex-shrink: 0;
}

/* Mobile styles - breakpoint at 1024px */
@media (max-width: 1024px) {
  .header__inner {
    padding: 0.5rem 1rem;
  }
  
  .header__content {
    gap: 1rem;
  }
  
  /* Ensure logo stays visible */
  .header__logo {
    display: block !important;
    visibility: visible !important;
    order: 1;
  }
  
  .header__logo-img {
    height: 40px;
    display: block !important;
  }
  
  /* Show hamburger on right */
  .header__hamburger {
    display: block;
    order: 3;
    margin-left: auto;
    margin-right: 5%;
  }
  
  /* Hide desktop nav and account */
  .nav--desktop {
    display: none;
  }
  
  .header__account--desktop {
    display: none;
  }
  
  /* Show mobile navigation */
  .nav--mobile {
    display: flex;
    position: fixed;
    top: 60px;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: #3867ae;
    flex-direction: column;
    justify-content: flex-start;
    align-items: stretch;
    padding: 2rem 0;
    transform: translateX(100%);
    transition: transform 0.3s ease;
    overflow-y: auto;
    box-shadow: -2px 0 10px rgba(0, 0, 0, 0.1);
  }
  
  .nav--mobile.nav--open {
    transform: translateX(0);
  }
  
  .nav--mobile .nav__list {
    flex-direction: column;
    gap: 0;
    width: 100%;
  }
  
  .nav--mobile .nav__item {
    width: 100%;
    border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  }
  
  .nav--mobile .nav__link,
  .nav--mobile .phone__link {
    display: block;
    padding: 1rem 2rem;
    font-size: 1.1rem;
  }
  
  /* Mobile auth links */
  .auth-links--mobile {
    flex-direction: column;
    gap: 1rem;
    padding: 1.5rem 2rem;
    margin-top: 1rem;
    border-top: 1px solid rgba(255, 255, 255, 0.2);
  }
  
  .auth-links--mobile .auth-links__item {
    width: 100%;
  }
  
  .auth-links--mobile .auth-links__link {
    display: block;
    text-align: center;
    padding: 0.75rem 1rem;
  }
  
  /* Mobile user info */
  .user-info--mobile {
    flex-direction: column;
    gap: 1rem;
    padding: 1.5rem 2rem;
    margin-top: 1rem;
    border-top: 1px solid rgba(255, 255, 255, 0.2);
    align-items: stretch;
  }
  
  .user-info--mobile .user-name {
    padding: 0.75rem 1rem;
    text-align: center;
    border: 2px solid white;
    border-radius: 4px;
  }
  
  .user-info--mobile .logout-btn {
    width: 100%;
  }
  
  /* Prevent body scroll when menu is open */
  body:has(.menu-toggle:checked) {
    overflow: hidden;
  }
}

/* Tablet and smaller desktop optimization (above mobile breakpoint) */
@media (min-width: 1025px) and (max-width: 1200px) {
  .header__inner {
    max-width: 100%;
    padding: 0.5rem 1.5rem;
  }
  
  .header__content {
    gap: 1rem;
  }
  
  .nav__list {
    gap: 1rem;
  }
  
  .nav__link,
  .phone__link {
    font-size: 0.9rem;
  }
  
  .auth-links {
    gap: 0.5rem;
  }
  
  .auth-links__link,
  .logout-btn {
    padding: 0.4rem 0.8rem;
    font-size: 1rem;
  }
}
</style>